import React from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Box, CircularProgress } from "@mui/material";

import { useAccessPermissions, useHasOrgReadAccessAnywhere } from "./useAccessPermissions";
import { PERMISSIONS } from "utils/accessManagement/permissionKeys";
import UnauthorizedAccess from "./UnauthorizedAccess";

// Guards /global-orgs (the cross-org list of every organization). Every role
// in the system — global (EnterpriseAdmin, OperationsAdmin, ...) and per-org
// (OrganizationOwner, OrganizationApprover, Leadership) alike — grants
// organization:read, so anyone holding it via ANY role should see the list;
// RequirePermission's plain usePermission check only sees GLOBAL roles (see
// useAccessPermissions), so a user who is e.g. OrganizationOwner of a single
// org (a per-org role assignment) was wrongly blocked here even though their
// role's own permissions[] include organization:read. OrgsList itself already
// scopes per-row edit access correctly via canEditOrgId, so admitting anyone
// with org-read access anywhere just lets them see the (read-only, for them)
// list, matching this route's original intent.
const RequireGlobalOrgReadRole = () => {
  const location = useLocation();
  const authToken = useSelector((state) => state.auth?.authToken);

  const { isLoading: isLoadingPermissions } = useAccessPermissions();
  const { hasAccess, isLoading: isLoadingOrgReadAccess } = useHasOrgReadAccessAnywhere(
    PERMISSIONS.ORGANIZATION_READ
  );

  if (!authToken) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  if (isLoadingPermissions || isLoadingOrgReadAccess) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", padding: "4rem" }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!hasAccess) {
    return (
      <UnauthorizedAccess
        message={`You do not have the required permission (${PERMISSIONS.ORGANIZATION_READ}) to access this page.`}
      />
    );
  }

  return <Outlet />;
};

export default RequireGlobalOrgReadRole;
